<template>
  <FormComponent
    title="Edit & Retry Failed DA"
    :backRoute="backRoute"
    :nonFieldErrors="formErrors.non_field_errors"
  >
    <template #formActions>
      <v-btn
        color="primary"
        depressed
        :disabled="!valid1"
        :loading="submitting"
        @click="submitForm"
      >
        Submit
      </v-btn>
      <v-btn color="secondary" class="ml-2" depressed @click="resetForm">
        Reset
      </v-btn>
    </template>

    <template #formContent>
      <v-form ref="mainForm" v-model="valid1">
        <v-row class="mx-2 my-0">

          <!-- ─── Basic Details ─── -->
          <v-col cols="12" class="Form-Columns-Dense pb-0">
            <span class="text-h6 font-weight-regular">Basic Details</span>
            <v-divider class="mt-1 mb-3" />
          </v-col>

          <v-col cols="12" lg="4" class="Form-Columns-Dense">
            <InputField
              type="text"
              label="BOE Number *"
              :rules="[(v) => !!v || 'BOE is required']"
              hide-details="auto"
              v-model="form.boe"
              :error-messages="formErrors.boe"
              @input="delete formErrors.boe"
            />
          </v-col>

          <v-col cols="12" lg="4" class="Form-Columns-Dense">
            <InputField
              type="text"
              label="Customer Name *"
              :rules="[(v) => !!v || 'Customer is required']"
              hide-details="auto"
              v-model="form.customer"
              :error-messages="formErrors.customer"
              @input="delete formErrors.customer"
            />
          </v-col>

          <v-col cols="12" lg="4" class="Form-Columns-Dense">
            <InputField
              type="text"
              label="Lane (Source → Destination) *"
              :rules="[(v) => !!v || 'Lane is required']"
              hide-details="auto"
              v-model="form.lane"
              :error-messages="formErrors.lane"
              @input="delete formErrors.lane"
            />
          </v-col>

          <v-col cols="12" lg="4" class="Form-Columns-Dense">
            <MultiSelect
              label="Job Type *"
              :items="jobTypeOptions"
              itemText="display_name"
              itemValue="value"
              hide-details="auto"
              v-model="form.job_type"
              :rules="[(v) => !!v || 'Job Type is required']"
              :error-messages="formErrors.job_type"
              @change="delete formErrors.job_type"
            />
          </v-col>

          <v-col cols="12" lg="4" class="Form-Columns-Dense">
            <DateField
              :inputProps="{
                dense: true,
                label: 'Movement Date *',
                rules: [(v) => !!v || 'Movement Date is required'],
                'hide-details': 'auto',
                'error-messages': formErrors.movement_date,
              }"
              v-model="form.movement_date"
              @input="delete formErrors.movement_date"
            />
          </v-col>

          <v-col cols="12" lg="4" class="Form-Columns-Dense">
            <InputField
              type="time"
              label="Movement Time *"
              :rules="[(v) => !!v || 'Movement Time is required']"
              hide-details="auto"
              v-model="form.movement_time"
              :error-messages="formErrors.movement_time"
              @input="delete formErrors.movement_time"
            />
          </v-col>

          <v-col cols="12" lg="4" class="Form-Columns-Dense">
            <MultiSelect
              label="Depot *"
              :items="depotList"
              itemText="depot_name"
              itemValue="depot_name"
              hide-details="auto"
              v-model="form.depot"
              :rules="[(v) => !!v || 'Depot is required']"
              :error-messages="formErrors.depot"
              @change="delete formErrors.depot"
            />
          </v-col>

          <!-- ─── Container Details ─── -->
          <v-col cols="12" class="Form-Columns-Dense pb-0 pt-4">
            <span class="text-h6 font-weight-regular">Container Details</span>
            <v-divider class="mt-1 mb-3" />
          </v-col>

          <v-col cols="12" lg="4" class="Form-Columns-Dense">
            <InputField
              type="text"
              placeholder="ABCD1234567"
              label="Container Number *"
              :rules="[(v) => !!v || 'Container Number is required']"
              hide-details="auto"
              v-model="form.container_number"
              :error-messages="formErrors.container_number"
              @input="delete formErrors.container_number"
            />
          </v-col>

          <v-col cols="12" lg="4" class="Form-Columns-Dense">
            <InputField
              type="text"
              label="Container Type *"
              :rules="[(v) => !!v || 'Container Type is required']"
              hide-details="auto"
              v-model="form.container_type"
              :error-messages="formErrors.container_type"
              @input="delete formErrors.container_type"
            />
          </v-col>

          <v-col cols="12" lg="4" class="Form-Columns-Dense">
            <InputField
              type="number"
              step=".01"
              label="Container Weight (Tons) *"
              :rules="[(v) => !!v || 'Container Weight is required']"
              hide-details="auto"
              v-model="form.container_weight"
              :error-messages="formErrors.container_weight"
              @input="delete formErrors.container_weight"
            />
          </v-col>

          <!-- ─── Addresses ─── -->
          <v-col cols="12" class="Form-Columns-Dense pb-0 pt-4">
            <span class="text-h6 font-weight-regular">Addresses</span>
            <v-divider class="mt-1 mb-3" />
          </v-col>

          <v-col cols="12" lg="4" class="Form-Columns-Dense">
            <InputField
              type="text"
              label="Source Address"
              hide-details="auto"
              v-model="form.source_address"
              :error-messages="formErrors.source_address"
              @input="delete formErrors.source_address"
            />
          </v-col>

          <v-col cols="12" lg="4" class="Form-Columns-Dense">
            <InputField
              type="text"
              label="Mid Address"
              hide-details="auto"
              v-model="form.mid_address"
              :error-messages="formErrors.mid_address"
              @input="delete formErrors.mid_address"
            />
          </v-col>

          <v-col cols="12" lg="4" class="Form-Columns-Dense">
            <InputField
              type="text"
              label="Destination Address"
              hide-details="auto"
              v-model="form.destination_address"
              :error-messages="formErrors.destination_address"
              @input="delete formErrors.destination_address"
            />
          </v-col>

          <!-- ─── Sub-hire ─── -->
          <v-col cols="12" class="Form-Columns-Dense pb-0 pt-4">
            <span class="text-h6 font-weight-regular">Sub-hire & Flags</span>
            <v-divider class="mt-1 mb-3" />
          </v-col>

          <v-col cols="12" lg="2" class="Form-Columns-Dense">
            <v-switch
              :label="`Sub Hire : ${form.is_subhire ? 'Yes' : 'No'}`"
              hide-details="auto"
              v-model="form.is_subhire"
              class="mt-1"
            />
          </v-col>

          <template v-if="form.is_subhire">
            <v-col cols="12" lg="3" class="Form-Columns-Dense">
              <InputField
                type="text"
                label="Vendor Code"
                hide-details="auto"
                v-model="form.vendor_code"
                :error-messages="formErrors.vendor_code"
                @input="delete formErrors.vendor_code"
              />
            </v-col>
            <v-col cols="12" lg="3" class="Form-Columns-Dense">
              <InputField
                type="number"
                step=".01"
                label="Vendor Rate"
                hide-details="auto"
                v-model="form.vendor_rate"
                :error-messages="formErrors.vendor_rate"
                @input="delete formErrors.vendor_rate"
              />
            </v-col>
          </template>

          <v-col cols="12" lg="2" class="Form-Columns-Dense">
            <v-switch
              :label="`Hazmat : ${form.is_hazmat ? 'Yes' : 'No'}`"
              hide-details="auto"
              v-model="form.is_hazmat"
              class="mt-1"
            />
          </v-col>

          <v-col cols="12" lg="2" class="Form-Columns-Dense">
            <v-switch
              :label="`Genset : ${form.has_genset ? 'Yes' : 'No'}`"
              hide-details="auto"
              v-model="form.has_genset"
              class="mt-1"
            />
          </v-col>

          <!-- ─── Previous Errors ─── -->
          <v-col v-if="lastErrors" cols="12" class="pt-4">
            <v-alert type="error" dense outlined>
              <strong>Previous rejection reason:</strong>
              <pre style="white-space:pre-wrap;font-size:12px;margin-top:4px;">{{ formatErrors(lastErrors) }}</pre>
            </v-alert>
          </v-col>

        </v-row>
      </v-form>
    </template>
  </FormComponent>
</template>

<script>
import FormComponent from "@/components/General/FormComponent.vue";
import InputField from "@/components/FormBaseComponents/InputField.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";
import { isAuthenticated, hasRouteAccess, getPermissions } from "@/utils/functions";

const defaultForm = () => ({
  boe: null,
  customer: null,
  lane: null,
  job_type: null,
  depot: null,
  movement_date: null,
  movement_time: null,
  container_number: null,
  container_type: null,
  container_weight: null,
  source_address: null,
  mid_address: null,
  destination_address: null,
  is_subhire: false,
  vendor_code: null,
  vendor_rate: null,
  is_hazmat: false,
  has_genset: false,
});

export default {
  components: { FormComponent, InputField, MultiSelect, DateField },

  data() {
    return {
      valid1: true,
      submitting: false,
      recordId: null,
      lastErrors: null,
      form: defaultForm(),
      formErrors: {},
      depotList: [],
      jobTypeOptions: [],
      backRoute: { name: "app-admin-integration-logs-inbound-integration-api" },
    };
  },

  methods: {
    formatErrors(errors) {
      if (!errors) return "";
      if (typeof errors === "string") return errors;
      return JSON.stringify(errors, null, 2);
    },

    populateForm(requestData) {
      if (!requestData) return;
      const f = defaultForm();
      Object.keys(f).forEach((key) => {
        if (key in requestData && requestData[key] !== undefined && requestData[key] !== null) {
          f[key] = requestData[key];
        }
      });
      this.form = f;
    },

    resetForm() {
      const stored = localStorage.getItem("bulkDARetryRecord");
      if (stored) {
        try {
          const record = JSON.parse(stored);
          this.populateForm(record.request_data);
        } catch (e) {
          this.form = defaultForm();
        }
      } else {
        this.form = defaultForm();
      }
      this.formErrors = {};
      if (this.$refs.mainForm) this.$refs.mainForm.resetValidation();
    },

    submitForm() {
      if (!this.$refs.mainForm.validate()) return;
      this.submitting = true;
      this.$bus.$emit("showLoader", true);

      this.$api.integration
        .retryBulkDARecord(this.recordId, this.form)
        .then((res) => {
          this.submitting = false;
          this.$bus.$emit("showLoader", false);
          const daList = (res.da_nos || []).join(", ");
          this.$bus.$emit("showToastMessage", {
            message: daList
              ? `DA created successfully: ${daList}`
              : "DA created successfully!",
            color: "success",
          });
          this.$router.push(this.backRoute);
        })
        .catch((err) => {
          this.submitting = false;
          this.$bus.$emit("showLoader", false);
          this.formErrors = err.data || {};
          this.$bus.$emit("showToastMessage", {
            message: "Retry failed. Please fix the errors and try again.",
            color: "error",
          });
        });
    },

    getRecord() {
      this.$bus.$emit("showLoader", true);
      this.$api.integration
        .getBulkDARecord(this.recordId)
        .then((res) => {
          const record = res.data;
          this.lastErrors = record.errors;
          this.populateForm(record.request_data);
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    getDepotList() {
      this.$api.depot
        .getDepotSelectList({ limit: "all" })
        .then((res) => { this.depotList = res.data; })
        .catch(console.error);
    },

    getJobTypeOptions() {
      this.$api.bmr
        .getBMROptions()
        .then((res) => {
          this.jobTypeOptions = res.data.actions.POST.job_type.choices;
        })
        .catch(console.error);
    },
  },

  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      const permissions = getPermissions("integration");
      if (!permissions || !permissions.change) {
        this.$bus.$emit("showToastMessage", {
          message: "You don't have access to this page",
          color: "error",
        });
        this.$router.push(
          `/app/${localStorage.getItem("user_type")?.toLowerCase() ?? "error"}`
        );
        return;
      }

      this.recordId = this.$route.params.id;
      this.getRecord();
      this.getDepotList();
      this.getJobTypeOptions();
    }
  },
};
</script>
